import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/leuteliaApi";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, ReferenceLine, LabelList,
} from "recharts";
import { Filter, Loader2, CheckCircle2, MinusCircle, Archive } from "lucide-react";
import { TT } from "./ChartKit";

const PRI = { CRITICAL: "Critica", HIGH: "Alta", MEDIUM: "Media", LOW: "Bassa" };
const REASON = {
  ADMITTED: { label: "Compare in Panoramica", icon: CheckCircle2, cls: "text-emerald-700" },
  BELOW_THRESHOLD: { label: "Sotto la soglia", icon: MinusCircle, cls: "text-brand-orange" },
  NOT_ACTIVE: { label: "Già risolta", icon: Archive, cls: "text-brand-muted" },
};

export default function Materialita({ ccu, scope, reload }) {
  const gate = ccu.materiality_gate;
  const [thr, setThr] = useState(gate.threshold);
  const [saving, setSaving] = useState(false);
  useEffect(() => setThr(gate.threshold), [gate.threshold, ccu.understanding_version]);

  const preview = gate.entries.map((e) => ({
    ...e,
    wouldAdmit: e.reason !== "NOT_ACTIVE" && e.materiality_score >= thr,
    name: e.title.length > 34 ? e.title.slice(0, 33) + "…" : e.title,
    pct: Math.round(e.materiality_score * 100),
  }));
  const admitted = preview.filter((e) => e.wouldAdmit);
  const excluded = preview.filter((e) => !e.wouldAdmit);
  const dirty = Math.abs(thr - gate.threshold) > 1e-9;

  const save = async () => {
    setSaving(true);
    try {
      const next = await api.setMateriality({ tenant: scope.tenant, hotel: scope.hotel, threshold: thr });
      toast.success(next.understanding_version !== ccu.understanding_version
        ? `Soglia aggiornata: comprensione v${next.understanding_version}`
        : "Soglia salvata: nulla cambia in Panoramica");
      await reload();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <section className="mb-8" data-testid="materiality-section">
      <div className="flex items-center gap-2.5 mb-3">
        <Filter size={20} className="text-brand-gold" strokeWidth={1.9} />
        <div>
          <h3 className="font-serif text-[20px] text-brand-ink leading-none">Cosa conta davvero</h3>
          <p className="text-[12.5px] text-brand-muted mt-1">Decidi tu quanto deve essere importante una segnalazione per comparire in Panoramica. Ogni modifica viene salvata nello storico.</p>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-brand-gold/25 p-6 grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-7">
        <div>
          <div className="flex items-end justify-between">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wide text-brand-muted">Soglia di importanza</div>
              <div className="font-serif text-[40px] text-brand-ink leading-none mt-1" data-testid="materiality-threshold-value">{Math.round(thr * 100)}%</div>
            </div>
            <div className="text-[12px] text-brand-muted font-mono">attiva: {Math.round(gate.threshold * 100)}% · v{ccu.understanding_version}</div>
          </div>
          <div className="mt-5 px-1">
            <Slider data-testid="materiality-slider" value={[thr]} min={0} max={1} step={0.05} onValueChange={(v) => setThr(v[0])} />
            <div className="flex justify-between text-[10.5px] text-brand-muted mt-1.5 font-mono"><span>0% · tutto entra</span><span>100% · solo l'essenziale</span></div>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
              <div className="text-[11px] font-bold uppercase tracking-wide text-emerald-700">Entrano</div>
              <div className="font-serif text-[28px] text-emerald-800 leading-none mt-1" data-testid="materiality-admitted-count">{admitted.length}</div>
            </div>
            <div className="rounded-lg bg-brand-cream border border-brand-gold/30 p-3">
              <div className="text-[11px] font-bold uppercase tracking-wide text-brand-orange">Restano fuori</div>
              <div className="font-serif text-[28px] text-brand-ink leading-none mt-1" data-testid="materiality-excluded-count">{excluded.length}</div>
            </div>
          </div>
          <Button data-testid="materiality-save-btn" disabled={!dirty || saving} onClick={save}
            className="mt-5 h-11 w-full rounded-lg bg-brand-green hover:bg-brand-greenDark text-brand-cream text-[13.5px] font-semibold disabled:opacity-50">
            {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
            {dirty ? "Applica la soglia" : "Soglia già applicata"}
          </Button>
        </div>

        <div>
          <div className="text-[13px] font-semibold text-brand-ink">Importanza di ogni segnalazione rispetto alla soglia</div>
          <div className="text-[11.5px] text-brand-muted mb-2">Verde = compare · Oro = sotto la soglia · Grigio = già risolta</div>
          {preview.length === 0 && (
            <div className="rounded-lg border border-dashed border-brand-gold/40 bg-brand-cream/40 px-5 py-8 text-center" data-testid="materiality-empty">
              <div className="font-serif text-[18px] text-brand-ink">Nessuna segnalazione da filtrare</div>
              <p className="text-[12.5px] text-brand-muted mt-1.5 max-w-[420px] mx-auto leading-snug">Le segnalazioni nascono solo da dati reali letti dalle fonti e dalle analisi profonde dei moduli. Avvia le analisi (Sito, Fotografia, Recensioni, Social) e torna qui: il grafico mostrerà l'importanza di ogni segnalazione rispetto alla soglia.</p>
            </div>
          )}
          {preview.length > 0 && <ResponsiveContainer width="100%" height={Math.max(160, preview.length * 34)}>
            <BarChart data={preview} layout="vertical" margin={{ left: 8, right: 36 }}>
              <XAxis type="number" domain={[0, 100]} hide />
              <YAxis type="category" dataKey="name" width={170} tick={{ fontSize: 11, fill: "#6F6A5F" }} axisLine={false} tickLine={false} />
              <Tooltip {...TT} cursor={{ fill: "#F5F1E8" }} formatter={(v) => [`${v}%`, "importanza"]} />
              <ReferenceLine x={Math.round(thr * 100)} stroke="#C2662E" strokeDasharray="4 3" label={{ value: "soglia", position: "top", fontSize: 10, fill: "#C2662E" }} />
              <Bar dataKey="pct" radius={[0, 6, 6, 0]} barSize={16}>
                {preview.map((e, i) => <Cell key={i} fill={e.reason === "NOT_ACTIVE" ? "#CFC8B8" : e.wouldAdmit ? "#16493A" : "#C6902A"} />)}
                <LabelList dataKey="pct" position="right" formatter={(v) => `${v}%`} style={{ fontSize: 11, fill: "#211F1A" }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>}
          <div className="mt-3 divide-y divide-brand-gold/15">
            {preview.map((e) => {
              const key = e.reason === "NOT_ACTIVE" ? "NOT_ACTIVE" : e.wouldAdmit ? "ADMITTED" : "BELOW_THRESHOLD";
              const R = REASON[key];
              return (
                <div key={e.finding_id} className="flex items-center gap-3 py-2" data-testid={`gate-entry-${e.finding_id}`}>
                  <R.icon size={16} className={`${R.cls} shrink-0`} />
                  <span className="text-[13px] text-brand-ink flex-1 truncate">{e.title}</span>
                  <span className="text-[10.5px] font-bold text-brand-muted">{PRI[e.priority_category]}</span>
                  <span className={`text-[11px] font-semibold ${R.cls} w-[150px] text-right`}>{R.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
